import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { useAuth } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatRelativeDate } from '@/lib/format-relative-date';
import {
  Loader2,
  Send,
  CheckCircle,
  Eye,
  EyeOff,
  MousePointerClick,
  AlertOctagon,
  AlertTriangle,
  UserMinus,
  Flag,
  Ban,
  Clock,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Calendar,
  Search,
} from 'lucide-react';

interface EventRow {
  email: string;
  event: string;
  date: string;
  subject: string;
  sender: string;
  project_id: string;
  message_id: string;
  reason?: string;
}

interface Stats {
  total: number;
  sent: number;
  delivered: number;
  opened: number;
  proxy_opened: number;
  clicked: number;
  hard_bounced: number;
  soft_bounced: number;
  unsubscribed: number;
  complained: number;
  blocked: number;
  deferred: number;
  first_event: string | null;
  last_event: string | null;
}

interface Meta {
  projects: Array<{ id: string; name: string }>;
  eventTypes: string[];
  senders: string[];
}

interface Filters {
  event: string;
  email: string;
  project: string;
  sender: string;
  dateFrom: string;
  dateTo: string;
  page: number;
  order: string;
}

const defaultFilters: Filters = {
  event: 'all',
  email: '',
  project: 'all',
  sender: '',
  dateFrom: '',
  dateTo: '',
  page: 1,
  order: 'desc',
};

const EVENT_CONFIG: Record<string, { label: string; icon: any; color: string; bg: string }> = {
  sent: { label: 'Sent', icon: Send, color: 'text-gray-600', bg: 'bg-gray-100' },
  delivered: { label: 'Delivered', icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-100' },
  opened: { label: 'Opened', icon: Eye, color: 'text-blue-600', bg: 'bg-blue-100' },
  uniqueOpened: { label: 'Opened', icon: Eye, color: 'text-blue-600', bg: 'bg-blue-100' },
  unique_opened: { label: 'Opened', icon: Eye, color: 'text-blue-600', bg: 'bg-blue-100' },
  proxy_open: { label: 'Proxy Open', icon: EyeOff, color: 'text-slate-600', bg: 'bg-slate-100' },
  unique_proxy_open: { label: 'Proxy Open', icon: EyeOff, color: 'text-slate-600', bg: 'bg-slate-100' },
  loadedByProxy: { label: 'Proxy Open', icon: EyeOff, color: 'text-slate-600', bg: 'bg-slate-100' },
  clicked: { label: 'Clicked', icon: MousePointerClick, color: 'text-purple-600', bg: 'bg-purple-100' },
  uniqueClicks: { label: 'Clicked', icon: MousePointerClick, color: 'text-purple-600', bg: 'bg-purple-100' },
  unique_clicks: { label: 'Clicked', icon: MousePointerClick, color: 'text-purple-600', bg: 'bg-purple-100' },
  hardBounce: { label: 'Hard Bounce', icon: AlertOctagon, color: 'text-red-600', bg: 'bg-red-100' },
  softBounce: { label: 'Soft Bounce', icon: AlertTriangle, color: 'text-orange-500', bg: 'bg-orange-100' },
  unsubscribed: { label: 'Unsubscribed', icon: UserMinus, color: 'text-orange-600', bg: 'bg-orange-100' },
  complaint: { label: 'Complaint', icon: Flag, color: 'text-rose-600', bg: 'bg-rose-100' },
  blocked: { label: 'Blocked', icon: Ban, color: 'text-slate-600', bg: 'bg-slate-100' },
  deferred: { label: 'Deferred', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-100' },
};

function getEventBadge(event: string) {
  const config = EVENT_CONFIG[event] || { label: event, icon: Send, color: 'text-gray-600', bg: 'bg-gray-100' };
  const Icon = config.icon;
  return (
    <Badge variant="outline" className={`${config.bg} ${config.color} border-0 gap-1 whitespace-nowrap`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

export default function EventsExplorer() {
  const { user } = useAuth();
  const timezone = user?.timezone || 'UTC';
  console.log('EventsExplorer timezone:', timezone, 'user:', user);
  const [filters, setFilters] = useState<Filters>({ ...defaultFilters });
  const [events, setEvents] = useState<EventRow[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [meta, setMeta] = useState<Meta | null>(null);

  // Загрузка мета-данных
  useEffect(() => {
    api.getEventsExplorerMeta().then(setMeta).catch(() => {});
  }, []);

  // Загрузка статистики
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const apiFilters: Record<string, any> = {};
      if (filters.event !== 'all') apiFilters.event = filters.event;
      if (filters.email) apiFilters.email = filters.email;
      if (filters.project !== 'all') apiFilters.project = filters.project;
      if (filters.sender) apiFilters.sender = filters.sender;
      if (filters.dateFrom) apiFilters.dateFrom = filters.dateFrom;
      if (filters.dateTo) apiFilters.dateTo = filters.dateTo;

      const data = await api.getEventsExplorerStats(apiFilters);
      setStats(data);
    } catch {
      // ignore
    } finally {
      setStatsLoading(false);
    }
  }, [filters.event, filters.email, filters.project, filters.sender, filters.dateFrom, filters.dateTo]);

  // Загрузка событий
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const apiFilters: Record<string, any> = {
        page: filters.page,
        order: filters.order,
        sort: 'date',
      };
      if (filters.event !== 'all') apiFilters.event = filters.event;
      if (filters.email) apiFilters.email = filters.email;
      if (filters.project !== 'all') apiFilters.project = filters.project;
      if (filters.sender) apiFilters.sender = filters.sender;
      if (filters.dateFrom) apiFilters.dateFrom = filters.dateFrom;
      if (filters.dateTo) apiFilters.dateTo = filters.dateTo;

      const data = await api.getEventsExplorer(apiFilters);
      setEvents(data.events);
      setTotal(data.total);
      setPages(data.pages);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleRefresh = () => {
    fetchStats();
    fetchEvents();
  };

  const toggleOrder = () => {
    setFilters(prev => ({ ...prev, order: prev.order === 'asc' ? 'desc' : 'asc', page: 1 }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Events Explorer</h2>
          <p className="text-muted-foreground">
            {total.toLocaleString()} events
            {stats?.first_event && stats?.last_event && (
              <span className="ml-2 text-xs">
                ({formatRelativeDate(stats.first_event, timezone)} - {formatRelativeDate(stats.last_event, timezone)})
              </span>
            )}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Project Tabs */}
      {meta && meta.projects.length > 0 && (
        <div className="flex items-center gap-1 border-b">
          <button
            onClick={() => handleFilterChange('project', 'all')}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              filters.project === 'all'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            All
          </button>
          {meta.projects.map((p) => (
            <button
              key={p.id}
              onClick={() => handleFilterChange('project', p.id)}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                filters.project === p.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-2 grid-cols-4 md:grid-cols-6 lg:grid-cols-11">
          {[
            { key: 'sent', label: 'Sent', value: stats.sent },
            { key: 'delivered', label: 'Delivered', value: stats.delivered },
            { key: 'opened', label: 'Opened', value: stats.opened },
            { key: 'proxy_open', label: 'Proxy Open', value: stats.proxy_opened },
            { key: 'clicked', label: 'Clicked', value: stats.clicked },
            { key: 'hardBounce', label: 'Hard Bounce', value: stats.hard_bounced },
            { key: 'softBounce', label: 'Soft Bounce', value: stats.soft_bounced },
            { key: 'unsubscribed', label: 'Unsub', value: stats.unsubscribed },
            { key: 'complaint', label: 'Complaint', value: stats.complained },
            { key: 'blocked', label: 'Blocked', value: stats.blocked },
            { key: 'deferred', label: 'Deferred', value: stats.deferred },
          ].map((stat) => {
            const config = EVENT_CONFIG[stat.key] || { icon: Send, color: 'text-gray-600' };
            const Icon = config.icon;
            const isActive = filters.event === stat.key;
            return (
              <button
                key={stat.key}
                onClick={() => handleFilterChange('event', isActive ? 'all' : stat.key)}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  isActive ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                } ${statsLoading ? 'opacity-50' : ''}`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon className={`h-3.5 w-3.5 ${config.color}`} />
                  <span className="text-xs text-muted-foreground truncate">{stat.label}</span>
                </div>
                <div className="text-lg font-bold">{stat.value.toLocaleString()}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-3">
            {/* Email search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search email..."
                value={filters.email}
                onChange={(e) => handleFilterChange('email', e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Event type */}
            <Select value={filters.event} onValueChange={(v) => handleFilterChange('event', v)}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Event type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                {Object.entries(EVENT_CONFIG)
                  .filter(([, cfg], idx, arr) => arr.findIndex(([, c]) => c.label === cfg.label) === idx)
                  .map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
              </SelectContent>
            </Select>

            {/* Date from */}
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="pl-9 w-[160px]"
              />
            </div>

            {/* Date to */}
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="pl-9 w-[160px]"
              />
            </div>

            {/* Order toggle */}
            <Button variant="outline" size="sm" onClick={toggleOrder}>
              {filters.order === 'asc' ? 'Oldest first' : 'Newest first'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Events Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : events.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Send className="h-12 w-12 mb-4 opacity-50" />
              <p>No events found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[180px]">Date</TableHead>
                    <TableHead className="w-[120px]">Event</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead className="w-[150px]">Sender</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((ev, i) => (
                    <TableRow key={`${ev.message_id}-${ev.event}-${i}`}>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {formatRelativeDate(ev.date, timezone)}
                      </TableCell>
                      <TableCell>{getEventBadge(ev.event)}</TableCell>
                      <TableCell>
                        <div className="font-mono text-sm">{ev.email}</div>
                        {ev.reason && <div className="text-xs text-muted-foreground mt-0.5 truncate max-w-[300px]" title={ev.reason}>{ev.reason}</div>}
                      </TableCell>
                      <TableCell className="text-sm truncate max-w-[300px]" title={ev.subject}>
                        {ev.subject || <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground truncate" title={ev.sender}>
                        {ev.sender || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {pages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {filters.page} of {pages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(filters.page - 1)}
                      disabled={filters.page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(filters.page + 1)}
                      disabled={filters.page >= pages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
